/*
 * Scroll-snap carousel controls.
 *
 * The track scrolls natively — swipe, trackpad and keyboard all work with no
 * JavaScript. This only adds arrow buttons, and reveals them solely when the
 * content actually overflows, so they never appear over a track that cannot
 * move.
 */

if (!customElements.get('twa-carousel')) {
  class TwaCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      this.prev = this.querySelector('[data-carousel-prev]');
      this.next = this.querySelector('[data-carousel-next]');
      if (!this.track) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-carousel-advance]')) this.scrollByPage(1);
      }, options);

      this.onScroll = () => this.sync();
      this.track.addEventListener('scroll', this.onScroll, { passive: true });

      this.prev?.addEventListener('click', () => this.scrollByPage(-1), options);
      this.next?.addEventListener('click', () => this.scrollByPage(1), options);

      // Card widths are percentage based, so recompute when the box changes.
      if ('ResizeObserver' in window) {
        this.ro = new ResizeObserver(() => this.sync());
        this.ro.observe(this.track);
      }

      this.sync();
      if (this.hasAttribute('data-autoscroll') && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.pause = this.querySelector('[data-carousel-pause]');
        if (this.pause) this.pause.hidden = false;
        this.track.style.scrollSnapType = 'none';
        const stop = () => {
          cancelAnimationFrame(this.animation);
          this.track.style.scrollSnapType = '';
          if (this.pause) {
            this.pause.textContent = 'Scrolling paused';
            this.pause.setAttribute('aria-disabled', 'true');
          }
        };
        ['pointerdown', 'wheel', 'keydown', 'focusin'].forEach((type) => this.addEventListener(type, stop, options));
        this.pause?.addEventListener('click', stop, options);
        matchMedia('(prefers-reduced-motion: reduce)').addEventListener('change', stop, options);
        let previous;
        let position = this.track.scrollLeft;
        const tick = (now) => {
          if (previous && !document.hidden) {
            const max = this.track.scrollWidth - this.track.clientWidth;
            if (max > 2) {
              position = position >= max ? 0 : position + Math.min(now - previous, 50) * 0.025;
              this.track.scrollLeft = position;
            }
          }
          previous = now;
          this.animation = requestAnimationFrame(tick);
        };
        this.animation = requestAnimationFrame(tick);
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
      cancelAnimationFrame(this.animation);
      this.track?.removeEventListener('scroll', this.onScroll);
      this.ro?.disconnect();
    }

    scrollByPage(direction) {
      const card = this.track.querySelector(':scope > *');
      // Fall back to most of a viewport when there is no card to measure.
      const step = card
        ? card.getBoundingClientRect().width + (parseFloat(getComputedStyle(this.track).columnGap) || 0)
        : this.track.clientWidth * 0.8;
      if (this.hasAttribute('data-loop')) {
        const max = this.track.scrollWidth - this.track.clientWidth;
        const left = this.track.scrollLeft;
        const target =
          direction < 0 && left <= 2 ? max : direction > 0 && left >= max - 2 ? 0 : left + step * direction;
        this.track.scrollTo({
          left: target,
          behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
        });
        return;
      }
      if (this.hasAttribute('data-deck')) {
        const count = this.track.children.length;
        const index = (Math.round(this.track.scrollLeft / step) + direction + count) % count;
        this.track.scrollTo({
          left: index * step,
          behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
        });
        return;
      }
      this.track.scrollBy({
        left: step * direction,
        behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
      });
    }

    sync() {
      const { scrollLeft, scrollWidth, clientWidth } = this.track;
      const image = this.track.querySelector('img');
      if (image) this.style.setProperty('--carousel-image-center', `${image.getBoundingClientRect().height / 2}px`);
      // 2px tolerance: sub-pixel layout leaves a residue that never reaches 0.
      const overflows = scrollWidth - clientWidth > 2;
      const counter = this.querySelector('[data-carousel-counter]');
      if (counter)
        counter.textContent =
          Math.round(scrollLeft / Math.max(1, clientWidth)) + 1 + ' / ' + this.track.children.length;
      if (this.prev) this.prev.hidden = !overflows || scrollLeft <= 2;
      if (this.next) this.next.hidden = !overflows || scrollLeft >= scrollWidth - clientWidth - 2;
      if (this.hasAttribute('data-loop')) {
        if (this.prev) this.prev.hidden = !overflows;
        if (this.next) this.next.hidden = !overflows;
      }
      if (this.hasAttribute('data-deck')) {
        if (this.prev) this.prev.hidden = !overflows;
        if (this.next) this.next.hidden = !overflows;
        const index = Math.round(scrollLeft / Math.max(1, clientWidth));
        if (this.deckIndex !== index) {
          this.deckIndex = index;
          this.querySelectorAll('[data-deck-back]').forEach((back) => back.remove());
          for (let offset = 2; offset >= 1; offset--) {
            const source = this.track.children[(index + offset) % this.track.children.length]?.querySelector('img');
            if (!source || this.track.children.length < 2) continue;
            const back = source.cloneNode();
            back.alt = '';
            back.setAttribute('aria-hidden', 'true');
            back.dataset.deckBack = offset;
            // The fanned cards are part of the same target: a click anywhere
            // on the deck advances it, as the front card already does.
            back.dataset.carouselAdvance = '';
            back.removeAttribute('loading');
            this.prepend(back);
          }
        }
      }
    }
  }

  customElements.define('twa-carousel', TwaCarousel);
}
